import { useAsync, useDebouncedEffect } from "@react-hookz/web";
import React, { useMemo, useState } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import type { DropdownComponentProps } from "../../components/common/Dropdown";
import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import {
    useClearDimensionValueOnDependencyChange,
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../store/dimension.hydration";
import type { DropdownDimensionValue } from "../../store/dimension.value";
import { NotificationType, notificationStore } from "../../store/notification";
import type { DimensionTemplate } from "../../types/dimensions";
import { isLoading } from "../../utils/api.utils";
import { buildRequestParameter } from "../../utils/app.utils";
import {
    getAdvertiserId,
    getOpportunityId,
} from "../../utils/permission.utils";
import { DropdownComponent } from "../common/Dropdown";
import { filterItemByValueAndLabel } from "../common/DropdownHelpers";

import * as DropdownDimensionHelpers from "./DropdownDimensionHelpers";

export interface OpportunityDropdownDimensionProps extends DimensionTemplate {}

/**
 * @deprecated
 */
export const OpportunityDropdownDimension: React.FunctionComponent<
    OpportunityDropdownDimensionProps
> = (props) => {
    const { dependencies, id, configuration, optional = false } = props;
    const { label, tooltip } = configuration;

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [filter, setFilter] = useState("");
    const [items, setItems] = useState<DropdownItemData[]>([]);
    const rpMap = useMemo(
        () => ({
            value: `${id}_id`,
            label: `${id}_name`,
        }),
        [],
    );

    const [dimensionValue, setDimensionValue] =
        useSetDimensionValue<DropdownDimensionValue>(
            id,
            buildRequestParameter(rpMap),
        );

    const [inputRequestState, inputRequestAction] = useAsync(async () => {
        try {
            const advertiserId = getAdvertiserId();
            const body = {
                advertiserId,
            };
            const { data } = await (
                await getCdbApiClient()
            ).getSfOpportunities(body);

            const fetchData = DropdownDimensionHelpers.processItemMapper(
                data ?? [],
                rpMap,
            );
            setItems(fetchData);
            const opportunityId = getOpportunityId();
            if (opportunityId) {
                const targetOpportunity = fetchData.find(
                    (item) => item.value === opportunityId,
                );
                if (targetOpportunity) {
                    setDimensionValue({
                        value: opportunityId,
                        label: targetOpportunity.label,
                    });
                }
            }
        } catch (err: any) {
            upsertNotification(err.message, NotificationType.ERROR);
        }
    });

    useDebouncedEffect(
        () =>
            void (async () => {
                await inputRequestAction.execute();
            })(),
        [],
        300,
    );

    const disabled = useGetDimensionDisable(dependencies);

    useClearDimensionValueOnDependencyChange(id, dependencies);

    // set status
    useUpdateStatusOnChange(id, (value: DropdownDimensionValue) =>
        DropdownDimensionHelpers.processDimensionStatus(value, optional),
    );

    const filteredItems = useMemo(
        () => filterItemByValueAndLabel(items, filter),
        [filter, inputRequestState],
    );

    const dropdownProps: DropdownComponentProps = {
        dimension: id,
        label,
        tooltip,
        items: filteredItems,
        search: filter,
        onChange: (e) => {
            const value: DropdownDimensionValue = items.find(
                (item) => item.value === e,
            ) || {
                value: e,
                label: e,
            };
            setDimensionValue(value);
        },
        selectedValue: dimensionValue.value,
        setSearch: (e) => {
            setFilter(e);
        },
        loading: isLoading(inputRequestState.status),
        showItemsWhileLoading: inputRequestState.status === "loading",
        disabled,
        optional,
    };

    useDimensionHydration(id, dependencies, async () => {
        throw new Error("not implemented");
    });

    return <DropdownComponent {...dropdownProps} />;
};

export default OpportunityDropdownDimension;
